// The building map (concept D, Thomas 2026-10-08): a flat plan of the real building, one floor at a time, drawn over the
// room. Room names sit large inside the rooms, one you-are-here, plain floor buttons with the Court's own floor names and a
// small plan of each, the stairs and lifts marked, the west front as a tab over the steps. Choosing a room shows its
// picture, what it is for and the way there (a dashed walk to the lift when it is on another floor); Go takes you.
// On a phone the rooms are a list by floor with a picture each, the plan one tap away, the choice pinned at the bottom.
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import { GuardedLink } from '../../lib/leaveGuard';
import { useFocusTrap } from '../../lib/useFocusTrap';
import { useBackToClose } from '../../lib/useBackToClose';
import { useMatchMedia } from '../../hooks/useMatchMedia';
import { MQ_MOBILE } from '../../lib/breakpoints';
import { useStage, useStageStatus } from '../../world/WorldStage';
import { ROOM_ROUTES, roomForPath } from '../../world/rooms';
import {
  howTo, levelOf, loadPlan, LEVEL_WORD, LEVELS_DOWN, planAsset, route, type Level, type PlanMeta,
} from '../../world/plan';
import { Icon } from '../../ui/Icon';
import { SFX } from '../../world/sfx';
import './map.css';

/** What each floor holds for you, on its button. */
const FLOOR_SUB: Record<Level, string> = {
  4: 'Basketball Court', 3: 'Library', 2: 'nothing to visit', 1: 'Courtroom, Great Hall', G: 'Clerk, Public Information',
};
const SIDE_W = 300;
const FLOORS_W = 232;

function useViewportSize(): [number, number] {
  const read = (): [number, number] => (typeof window === 'undefined' ? [1280, 800] : [window.innerWidth, window.innerHeight]);
  const [size, setSize] = useState(read);
  useEffect(() => {
    const on = () => setSize(read());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return size;
}

interface Place { id: string; name: string; holds: string; picture?: string; level: Level; to?: string }

function placeOf(plan: PlanMeta, id: string, here: string): Place | null {
  const dest = ROOM_ROUTES[id];
  if (id === 'front') {
    return { id, name: plan.front.name, holds: dest.holds, picture: plan.front.picture, level: levelOf(plan, id, here) ?? '1', to: dest.to };
  }
  const r = plan.rooms[id];
  if (!r) return null;
  return { id, name: r.name, holds: dest?.holds ?? r.holds, picture: r.picture, level: r.floor, to: dest?.to };
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const floorName = (lv: Level) => (lv === 'G' ? 'Ground floor' : `${cap(LEVEL_WORD[lv])} floor`);

export default function MapDialog() {
  const stage = useStage();
  const { mapOpen } = useStageStatus();
  const location = useLocation();
  const trap = useFocusTrap<HTMLDivElement>(mapOpen);
  const phone = useMatchMedia(MQ_MOBILE);
  const [vw, vh] = useViewportSize();
  const [plan, setPlan] = useState<PlanMeta | null>(null);
  const [failed, setFailed] = useState(false);
  const [shown, setShown] = useState<Level>('1');
  const [sel, setSel] = useState<string | null>(null);
  const [view, setView] = useState<'rooms' | 'plan'>('rooms');

  const here = (stage?.room() ?? null) || roomForPath(location.pathname);

  useEffect(() => {
    if (!mapOpen) return;
    let live = true;
    loadPlan().then((p) => { if (live) { setPlan(p); setFailed(false); } }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [mapOpen]);

  // opens on the floor you are on, nothing chosen yet
  useEffect(() => {
    if (!mapOpen || !plan) return;
    setShown(levelOf(plan, here) ?? '1');
    setSel(null);
    setView('rooms');
  }, [mapOpen, plan, here]);

  // on a phone the list opens at your own room
  useEffect(() => {
    if (!mapOpen || !plan || !phone || view !== 'rooms') return;
    const row = document.querySelector<HTMLElement>('.map-row[aria-current="location"]');
    if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'center' });
  }, [mapOpen, plan, phone, view]);

  useBackToClose(mapOpen, () => stage?.setMapOpen(false));

  // Closed where it was opened (Escape, Close): focus goes back to the map
  // button. The trap restores whatever had focus at open, but WebKit does not
  // focus a button on click, so that was the page body and focus was lost
  // (and the loading view swapping for the plan strands it too). Go closes the
  // map a beat before the router moves, so the check waits a frame and only
  // acts if the page is still the one the map opened on and nothing else has
  // taken focus; a new screen keeps its own focus.
  const openedAt = useRef<string | null>(null);
  const pathNow = useRef(location.pathname);
  pathNow.current = location.pathname;
  useEffect(() => {
    if (mapOpen) {
      if (openedAt.current === null) openedAt.current = location.pathname;
      return undefined;
    }
    const was = openedAt.current;
    openedAt.current = null;
    if (was === null) return undefined;
    const frame = requestAnimationFrame(() => {
      if (pathNow.current !== was) return;
      const active = document.activeElement;
      if (active && active !== document.body) return;
      // The argument screen draws its own map button: take the one on screen.
      const buttons = Array.from(document.querySelectorAll<HTMLElement>('[data-testid="map-button"]'));
      (buttons.find((b) => b.getClientRects().length > 0) ?? buttons[0])?.focus();
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapOpen]);

  useEffect(() => {
    if (!mapOpen) return undefined;
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      stage?.setMapOpen(false);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [mapOpen, stage]);

  const places = useMemo(() => {
    if (!plan) return [] as Place[];
    const ids = [...Object.keys(plan.rooms), 'front'];
    return ids.map((id) => placeOf(plan, id, here)).filter((p): p is Place => p !== null);
  }, [plan, here]);

  if (!mapOpen || !stage) return null;
  const close = () => stage.setMapOpen(false);
  const pick = (id: string) => { SFX.click(); setSel(id); };
  const showFloor = (lv: Level) => { SFX.move(); setShown(lv); };
  const herePlace = plan ? placeOf(plan, here, here) : null;
  const selPlace = plan && sel ? placeOf(plan, sel, here) : null;

  if (!plan) {
    return (
      <div ref={trap} className="map-dialog" role="dialog" aria-modal="true" aria-labelledby="map-title" data-testid="map-dialog">
        <h2 id="map-title" className="map-head">Building map</h2>
        <p className="map-loading" role="status">{failed ? 'The map did not load. Close it and try again.' : 'Unfolding the plan'}</p>
        <button type="button" className="pb wood map-close" onClick={close} data-testid="map-close"><Icon name="close" /> Close</button>
      </div>
    );
  }

  const [pw, ph] = plan.size;
  // the plan's scale: the room between the floor buttons and the card on a desktop; the width on a phone
  const wide = vw >= 1100;
  const areaW = phone ? vw - 32 : wide ? vw - FLOORS_W - SIDE_W - 80 : vw - SIDE_W - 56;
  const areaH = phone ? vh - 260 : vh - 32;
  const k = Math.max(0.5, Math.min(areaW / pw, areaH / ph));

  const card = (
    <Card plan={plan} place={selPlace} here={here} onGo={close} compact={phone} />
  );

  const planView = (
    <PlanView plan={plan} level={shown} k={k} here={here} sel={sel} onPick={pick} places={places} />
  );

  if (phone) {
    return (
      <div ref={trap} className="map-dialog is-phone" role="dialog" aria-modal="true" aria-labelledby="map-title" data-testid="map-dialog">
        <div className="map-bar">
          <h2 id="map-title" className="map-title-sm">Map</h2>
          <div className="map-toggle" role="group" aria-label="Show">
            <button type="button" className={`pb sm ${view === 'rooms' ? 'brass' : 'paper'}`} aria-pressed={view === 'rooms'} onClick={() => setView('rooms')}>Rooms</button>
            <button type="button" className={`pb sm ${view === 'plan' ? 'brass' : 'paper'}`} aria-pressed={view === 'plan'} onClick={() => setView('plan')}>Plan</button>
          </div>
          <button type="button" className="pb wood sm map-close" onClick={close} data-testid="map-close" aria-label="Close the map"><Icon name="close" /></button>
        </div>
        {view === 'plan' && (
          <nav className="map-tabs" aria-label="Floors">
            {[...LEVELS_DOWN].reverse().map((lv) => (
              <button key={lv} type="button" className={`map-tab${shown === lv ? ' is-shown' : ''}`} aria-pressed={shown === lv} onClick={() => showFloor(lv)}>
                {lv === 'G' ? 'Ground' : cap(LEVEL_WORD[lv])}
              </button>
            ))}
          </nav>
        )}
        <div className="map-body" data-scroll="intended">
          {view === 'plan' ? planView : (
            <ul className="map-list" aria-label="Rooms by floor">
              {LEVELS_DOWN.slice().reverse().map((lv) => {
                const rows = places.filter((p) => p.level === lv);
                if (!rows.length) return null;
                return (
                  <li key={lv} className="map-list-floor">
                    <h3 className="map-head">{floorName(lv)}</h3>
                    <ul>
                      {rows.map((p) => (
                        <li key={p.id}>
                          <button
                            type="button"
                            className={`map-row${sel === p.id ? ' is-sel' : ''}${p.to ? '' : ' is-closed'}`}
                            aria-pressed={sel === p.id}
                            aria-current={p.id === here ? 'location' : undefined}
                            onClick={() => pick(p.id)}
                          >
                            {p.picture ? <img src={planAsset(p.picture)} alt="" className="map-row-pic" /> : <span className="map-row-pic" />}
                            <span className="map-row-text"><b>{p.name}</b><span>{p.holds}</span></span>
                            {p.id === here && <span className="map-you-tag">You</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="map-pinned">{card}</div>
      </div>
    );
  }

  return (
    <div
      ref={trap}
      className={`map-dialog is-desk${wide ? ' is-wide' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="map-title"
      data-testid="map-dialog"
      style={{ '--floors-w': `${FLOORS_W}px`, '--side-w': `${SIDE_W}px` } as CSSProperties}
    >
      <h2 id="map-title" className="sr-only">Building map, {floorName(shown).toLowerCase()}</h2>
      <nav className="map-floors" aria-label="Floors">
        <p className="map-head" aria-hidden="true">Floors</p>
        {LEVELS_DOWN.map((lv) => {
          const f = plan.floors.find((x) => x.id === lv);
          const empty = lv === '2';
          return (
            <button
              key={lv}
              type="button"
              className={`map-floor${shown === lv ? ' is-shown' : ''}${empty ? ' is-empty' : ''}`}
              aria-pressed={shown === lv}
              onClick={() => showFloor(lv)}
            >
              {f && <img src={planAsset(f.thumb)} alt="" className="map-floor-thumb" />}
              <span className="map-floor-text">
                <span className="map-floor-name">{floorName(lv)}</span>
                <span className="map-floor-sub">{FLOOR_SUB[lv]}</span>
              </span>
              {herePlace?.level === lv && <span className="map-floor-you" title="You are on this floor"><span className="sr-only">, you are here</span></span>}
              {selPlace && selPlace.level === lv && selPlace.id !== here && <span className="map-floor-flag" aria-hidden="true" />}
            </button>
          );
        })}
      </nav>
      <div className="map-plan-area">{planView}</div>
      <aside className="map-side" aria-label="Where you are and where to">
        <button type="button" className="pb wood sm map-close" onClick={close} data-testid="map-close"><Icon name="close" /> Close</button>
        <p className="map-head">You are here</p>
        <p className="map-here"><span className="map-chev" aria-hidden="true" />{herePlace ? `${herePlace.name}, ${floorName(herePlace.level).toLowerCase()}` : 'Somewhere in the building'}</p>
        <p className="map-head">Selected</p>
        {card}
      </aside>
      <p className="map-floor-title" aria-hidden="true">{floorName(shown)}</p>
    </div>
  );
}

function Card({ plan, place, here, onGo, compact }: { plan: PlanMeta; place: Place | null; here: string; onGo: () => void; compact: boolean }) {
  if (!place) {
    return <div className={`map-card is-empty${compact ? ' is-compact' : ''}`}><p>Choose a room{compact ? '' : ' on the plan'} to see what it holds and the way there.</p></div>;
  }
  const how = howTo(plan, here, place.id);
  const isHere = place.id === here;
  const label = place.to === '/home' && place.id !== 'clerk' ? 'Go to the docket' : 'Go';
  return (
    <div className={`map-card${compact ? ' is-compact' : ''}`} data-testid="map-card">
      {place.picture && <img src={planAsset(place.picture)} alt="" className="map-card-pic" />}
      <div className="map-card-text">
        <h3 className="map-card-name">{place.name}</h3>
        <p>{cap(place.holds)}. {floorName(place.level)}.</p>
        {how && !isHere && <p className="map-card-how">{how}</p>}
      </div>
      {isHere ? <p className="map-card-here">You are here.</p> : place.to ? (
        <GuardedLink to={place.to} guardLabel={place.name} className="pb brass map-go" onClick={onGo} data-testid="map-go">
          {label}
        </GuardedLink>
      ) : <p className="map-card-here">Not open to visitors.</p>}
    </div>
  );
}

function PlanView({ plan, level, k, here, sel, onPick, places }: {
  plan: PlanMeta; level: Level; k: number; here: string; sel: string | null; onPick: (id: string) => void; places: Place[];
}) {
  const [pw, ph] = plan.size;
  const f = plan.floors.find((x) => x.id === level);
  const walk = sel ? route(plan, here, sel, level) : null;
  const exit = plan.exits[level];
  const box = (r: [number, number, number, number]): CSSProperties => ({ left: r[0] * k, top: r[1] * k, width: r[2] * k, height: r[3] * k });
  /** A button's box: the room's, grown about its centre to a 44 px hit target where the plan draws it smaller. */
  const hit = (r: [number, number, number, number]): CSSProperties => {
    const w = Math.max(44, r[2] * k); const h = Math.max(44, r[3] * k);
    return { left: (r[0] + r[2] / 2) * k - w / 2, top: (r[1] + r[3] / 2) * k - h / 2, width: w, height: h };
  };
  const inRoom = (id: string) => plan.rooms[id]?.floor === level;
  const youAt: [number, number] | null = here === 'front'
    ? (exit ? [exit.rect[0] + exit.rect[2] / 2, exit.rect[1]] : null)
    : inRoom(here) ? [plan.rooms[here].rect[0] + plan.rooms[here].rect[2] / 2, plan.rooms[here].rect[1] + plan.rooms[here].rect[3] / 2 - 10] : null;
  const byId = new Map(places.map((p) => [p.id, p]));
  return (
    <div className="map-plan" style={{ width: pw * k, height: ph * k, '--k': k, '--map-f': `${Math.max(1, Math.floor(k)) * 16}px`, '--map-f2': `${Math.max(2, Math.floor(2 * k)) * 16}px` } as CSSProperties} data-level={level}>
      {f && <img src={planAsset(f.img)} alt="" className="map-plan-img" width={pw * k} height={ph * k} />}
      {plan.labels.filter((l) => l.floor === level).map((l) => (
        <span key={l.text} className="map-label" style={box(l.rect)} aria-hidden="true">{l.text}</span>
      ))}
      {Object.entries(plan.rooms).filter(([, r]) => r.floor === level).map(([id, r]) => {
        const p = byId.get(id);
        const big = r.big && r.name.length * 14 + 8 <= r.rect[2] && r.rect[3] >= 30;
        const cls = `map-room${big ? ' is-big' : ''}${id === here ? ' is-here' : ''}${sel === id ? ' is-sel' : ''}${p?.to ? '' : ' is-closed'}`;
        return (
          <button
            key={id}
            type="button"
            className={cls}
            style={{ ...hit(r.rect), color: r.ink }}
            aria-pressed={sel === id}
            aria-current={id === here ? 'location' : undefined}
            aria-label={`${r.name}, ${p?.holds ?? r.holds}`}
            onClick={() => onPick(id)}
          >
            <span>{r.name}</span>
          </button>
        );
      })}
      {exit && (
        <button
          type="button"
          className={`map-exit${sel === 'front' ? ' is-sel' : ''}${here === 'front' ? ' is-here' : ''}`}
          style={hit(exit.rect)}
          aria-pressed={sel === 'front'}
          aria-label={`${plan.front.name}, the way in`}
          onClick={() => onPick('front')}
        >
          <span className="map-exit-flag" aria-hidden="true" />{plan.front.name}
        </button>
      )}
      {walk && (
        <svg className="map-route" viewBox={`0 0 ${pw} ${ph}`} width={pw * k} height={ph * k} aria-hidden="true">
          <polyline points={walk.points.map((q) => q.join(',')).join(' ')} className="map-route-casing" />
          <polyline points={walk.points.map((q) => q.join(',')).join(' ')} className="map-route-line" />
        </svg>
      )}
      {walk?.lift && (
        <span className="map-lift-tag" style={{ left: (walk.lift.at[0] + 9) * k, top: (walk.lift.at[1] - 6) * k }}>{walk.lift.tag}</span>
      )}
      {youAt && (
        <span className="map-you" style={{ left: youAt[0] * k, top: youAt[1] * k }} aria-hidden="true"><span className="map-chev" /></span>
      )}
    </div>
  );
}
